import {
  Coupon1Icon,
  DeleteIcon,
  FormControl,
  FormControlLabel,
  FormLabel,
  GKButton,
  GKEditor,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  InfoItalicIcon,
  InputAdornment,
  NumberIcon,
  Plus1Icon,
  PopConfirm,
  RadioGroup,
  ReloadIcon,
  Show,
  Switch,
  Typography,
} from "components";
import { useFetchQuery } from "hooks/useQueries";
import { FeePlanListInterface } from "interfaces/PrivateRouteInterface";
import { CouponFormValues } from "interfaces/Revenue";
import DashboardLayout from "layouts/DashboardLayout";
import { generateRandomString } from "Pages/RevenueManagement/utils";
import { useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateCouponMutation,
  useDeleteCouponMutation,
  useGetCouponByIdQuery,
  useUpdateCouponMutation,
} from "store/features/Revenue/CouponApi";
import { scrollToFirstError, validationForPercentage } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";
import PhoneNumberModal from "./PhoneNumberModal";

const CouponForm = () => {
  useDocumentTitle("Coupon");

  const navigate = useNavigate();
  const { id } = useParams();

  const [feePlanList, setFeePlanList] = useState([]);
  const [openPhoneModal, setOpenPhoneModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { data, isFetching } = useGetCouponByIdQuery(id as string, {
    skip: !id,
  });

  const [createCoupon, { isLoading }] = useCreateCouponMutation();

  const [updateCoupon, { isLoading: updateCouponLoading }] =
    useUpdateCouponMutation();

  const [deleteCoupon, { isLoading: deleteCouponLoading }] =
    useDeleteCouponMutation();

  const { isFetching: feePlanFetching } = useFetchQuery({
    key: ["GET_FEE_PLANS_DROPDOWN"],
    route: `/api/revenue/fee-plan/dropdown/`,
    requestBody: {
      revenue_products: JSON.stringify(["subscription_fee"]),
    },
    success: (data: any) => {
      setFeePlanList(
        data?.data?.data.map((dataItem: FeePlanListInterface) => {
          return {
            name: dataItem.name,
            value: dataItem.value,
          };
        }),
      );
    },
  });

  const form = useForm<CouponFormValues>({
    defaultValues: {
      name: "",
      code: "",
      description: "",
      couponType: "",
      discountValue: "",
      maximumDiscountValue: 0,
      applyOn: "",
      validFrom: null,
      validTo: null,
      couponUsage: [{ phone: "" }],
      universalLimit: "",
      perUserLimit: "",
      isActive: true,
      feePlan: "",
    },
    values: data?.data,
  });

  const applyOnWatch = form.watch("applyOn");
  const couponTypeWatch = form.watch("couponType");

  const handleAutoGenerateCouponCode = () => {
    const generateCouponCode = generateRandomString(10);

    form.setValue("code", generateCouponCode);
    form.clearErrors("code");
  };

  return (
    <DashboardLayout title="Create Coupon" loading={id && isFetching}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(
            async (values: CouponFormValues) => {
              let data = values;

              if (values?.applyOn === "Universal") {
                data = {
                  ...data,
                  couponUsage: [],
                };
              }
              try {
                id
                  ? await updateCoupon({ id, body: data }).unwrap()
                  : await createCoupon(data).unwrap();
                navigate(-1);
              } catch (err) {
                console.error("Create coupon failed:", err);
              }
            },
            (errors) => scrollToFirstError(errors, form),
          )}
        >
          <Grid2
            container
            spacing={3}
            maxHeight={"calc(100vh - 155px)"}
            overflow={"auto"}
            minHeight={"calc(100vh - 155px)"}
            alignContent={"flex-start"}
          >
            <Grid2 size={6} container>
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography
                  fontSize={14}
                  fontWeight={700}
                  color={"var(--neutral-900)"}
                >
                  Basic Details
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="name"
                  control={form.control}
                  rules={{
                    required: {
                      value: true,
                      message: "Please enter a name",
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <GKTextField
                      {...field}
                      requiredField
                      inputLabel={"Coupon Name"}
                      error={!!error}
                      helperText={error?.message}
                      slotProps={{
                        input: {
                          endAdornment: <Coupon1Icon height={16} width={16} />,
                        },
                      }}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="couponType"
                  control={form.control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "var(--black)",
                        }}
                      >
                        Coupon Type
                      </FormLabel>
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="FIXED"
                          control={<GKRadio disableLeftPadding />}
                          label="Fix Amount (₹)"
                        />
                        <FormControlLabel
                          value="PERCENTAGE"
                          control={<GKRadio disableLeftPadding />}
                          label="Percentage (%)"
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid2>
              <Show.When isTrue={!!couponTypeWatch}>
                <Grid2 size={6}>
                  <Controller
                    name="discountValue"
                    control={form.control}
                    render={({ field }) => (
                      <GKTextField
                        {...field}
                        type="number"
                        value={
                          couponTypeWatch !== "FIXED"
                            ? validationForPercentage(+field.value)
                            : field.value
                        }
                        inputLabel={"Discount Value"}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                {couponTypeWatch === "FIXED" ? "₹" : "%"}
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Grid2>
                <Show.When isTrue={couponTypeWatch === "PERCENTAGE"}>
                  <Grid2 size={6}>
                    <Controller
                      name="maximumDiscountValue"
                      control={form.control}
                      render={({ field }) => (
                        <GKTextField
                          {...field}
                          type="number"
                          inputLabel={"Maximum Discount"}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  ₹
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      )}
                    />
                  </Grid2>
                </Show.When>
                <Grid2 size={couponTypeWatch === "PERCENTAGE" ? 12 : 6}>
                  <Controller
                    name="feePlan"
                    control={form.control}
                    rules={{
                      required: {
                        value: true,
                        message: "Please select fee plan",
                      },
                    }}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <GKSearchSelect
                          {...field}
                          requiredField
                          inputLabel="Fee Plan Name"
                          loading={feePlanFetching}
                          disableClearable
                          options={feePlanList || []}
                          onChange={(e, val) => field.onChange(val?.value)}
                          value={
                            feePlanList.find(
                              (dataItem) => dataItem.value === field.value,
                            ) || ""
                          }
                          readOnly={!!id}
                          getOptionLabel={(option) => option.name || option}
                          error={!!error}
                          helperText={error?.message}
                        />
                      );
                    }}
                  />
                </Grid2>
                <Grid2
                  container
                  size={12}
                  borderRadius={1}
                  bgcolor={"var(--grey-bg-100)"}
                  p={2}
                  gap={1}
                  alignItems={"center"}
                  spacing={"12px"}
                >
                  <Grid2 size={6}>
                    <Controller
                      name="code"
                      control={form.control}
                      rules={{
                        required: {
                          value: true,
                          message: "Please enter a code",
                        },
                        maxLength: {
                          value: 10,
                          message: "Code should have 10 character",
                        },
                        minLength: {
                          value: 10,
                          message: "Code should have 10 character",
                        },
                        pattern: {
                          value: /^[A-Za-z0-9]+$/,
                          message: "Special characters are not allowed",
                        },
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <GKTextField
                          {...field}
                          requiredField
                          inputLabel={"Coupon Code"}
                          onChange={(e) => {
                            const { value } = e.target;

                            if (/^[A-Za-z0-9]*$/.test(value)) {
                              field.onChange(value);
                            }
                          }}
                          error={!!error}
                          helperText={error?.message}
                          slotProps={{
                            input: { endAdornment: <NumberIcon /> },
                          }}
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <GKButton
                      variant="outlined"
                      color="dark"
                      startIcon={<ReloadIcon />}
                      sx={{ p: "14px", mt: "6px" }}
                      onClick={handleAutoGenerateCouponCode}
                    >
                      Auto Generate Code
                    </GKButton>
                  </Grid2>
                  <Grid2
                    size={12}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                    color={"var(--neutral-500)"}
                    fontSize={12}
                    fontWeight={500}
                  >
                    <InfoItalicIcon /> Coupon code must be 10 characters long.
                  </Grid2>
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name="isActive"
                    control={form.control}
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{ gap: 1 }}
                        control={<Switch {...field} checked={field.value} />}
                        labelPlacement="start"
                        label={
                          <Typography
                            color="var(--grey-900)"
                            fontSize={13}
                            fontWeight={600}
                          >
                            Set Coupon as Active
                          </Typography>
                        }
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={12} mt={2}>
                  <Typography
                    fontSize={14}
                    fontWeight={700}
                    color="var(--neutral-900)"
                  >
                    Restriction
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="var(--neutral-900)"
                  >
                    Who Can Use This Coupon?
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Controller
                    name="applyOn"
                    control={form.control}
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          value="Universal"
                          control={<GKRadio disableLeftPadding />}
                          label="Anyone"
                        />

                        <FormControlLabel
                          value="User Specific"
                          control={<GKRadio disableLeftPadding />}
                          label="Selected Users Only (by phone numbers)"
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid2>
                <Show.When isTrue={applyOnWatch === "Universal"}>
                  <Grid2 size={6}>
                    <Controller
                      name="universalLimit"
                      control={form.control}
                      render={({ field }) => (
                        <GKTextField inputLabel="Maximum Uses" {...field} />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <Controller
                      name="perUserLimit"
                      control={form.control}
                      render={({ field }) => (
                        <GKTextField
                          inputLabel="Maximum Uses per Person"
                          {...field}
                        />
                      )}
                    />
                  </Grid2>
                </Show.When>
                <Show.When isTrue={applyOnWatch === "User Specific"}>
                  <Grid2 size={12}>
                    <GKButton
                      variant="outlined"
                      color="dark"
                      startIcon={<Plus1Icon />}
                      onClick={() => setOpenPhoneModal(true)}
                    >
                      Add Manually
                    </GKButton>
                  </Grid2>
                  <Grid2 size={6}>
                    <Controller
                      name="perUserLimit"
                      control={form.control}
                      render={({ field }) => (
                        <GKTextField inputLabel="Uses per Person" {...field} />
                      )}
                    />
                  </Grid2>
                </Show.When>

                <Grid2 size={12}>
                  <Controller
                    name="description"
                    control={form.control}
                    render={({ field }) => (
                      <GKEditor
                        value={field.value}
                        onChange={field.onChange}
                        inputLabel="Description"
                        toolbar={[
                          "undo",
                          "redo",
                          "|",
                          "heading",
                          "bold",
                          "italic",
                          "underline",
                          "strikethrough",
                          "blockQuote",
                          "|",
                          "fontSize",
                          "fontColor",
                          "fontBackgroundColor",
                          "|",
                        ]}
                      />
                    )}
                  />
                </Grid2>
              </Show.When>
            </Grid2>
          </Grid2>
          <Grid2
            container
            mt={2}
            display={"flex"}
            gap={1}
            justifyContent={"flex-end"}
          >
            {!!id && (
              <GKButton
                variant="outlined"
                loading={deleteCouponLoading}
                color="error"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                startIcon={<DeleteIcon />}
              >
                Delete Coupon
              </GKButton>
            )}
            <GKButton
              variant="contained"
              type="submit"
              loading={isLoading || updateCouponLoading}
            >
              {id ? "Save" : "Create Coupon"}
            </GKButton>
          </Grid2>

          <Show.When isTrue={!!openPhoneModal}>
            <PhoneNumberModal
              open={openPhoneModal}
              setOpen={setOpenPhoneModal}
            />
          </Show.When>
        </form>
      </FormProvider>

      <Show.When isTrue={Boolean(anchorEl)}>
        <PopConfirm
          anchorEl={null}
          open={Boolean(anchorEl)}
          setOpen={setAnchorEl}
          onSuccess={() => deleteCoupon(id)}
          modalTitle="Delete Coupon?"
          message="Are you sure you want to delete this coupon? All of your data will be permanently removed. This action cannot be undone."
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default CouponForm;
